/**
 * An amount in Hindi words, as the farmer hears it read off the slip (F4.8):
 * 3667350 paise → "छत्तीस हज़ार छह सौ तिहत्तर रुपये पचास पैसे". Indian grouping (करोड़, लाख,
 * हज़ार, सौ); 1–99 are their own words in Hindi.
 */
const UPTO_99 = [
  'शून्य',
  'एक',
  'दो',
  'तीन',
  'चार',
  'पाँच',
  'छह',
  'सात',
  'आठ',
  'नौ',
  'दस',
  'ग्यारह',
  'बारह',
  'तेरह',
  'चौदह',
  'पंद्रह',
  'सोलह',
  'सत्रह',
  'अठारह',
  'उन्नीस',
  'बीस',
  'इक्कीस',
  'बाईस',
  'तेईस',
  'चौबीस',
  'पच्चीस',
  'छब्बीस',
  'सत्ताईस',
  'अट्ठाईस',
  'उनतीस',
  'तीस',
  'इकतीस',
  'बत्तीस',
  'तैंतीस',
  'चौंतीस',
  'पैंतीस',
  'छत्तीस',
  'सैंतीस',
  'अड़तीस',
  'उनतालीस',
  'चालीस',
  'इकतालीस',
  'बयालीस',
  'तैंतालीस',
  'चवालीस',
  'पैंतालीस',
  'छियालीस',
  'सैंतालीस',
  'अड़तालीस',
  'उनचास',
  'पचास',
  'इक्यावन',
  'बावन',
  'तिरेपन',
  'चौवन',
  'पचपन',
  'छप्पन',
  'सत्तावन',
  'अट्ठावन',
  'उनसठ',
  'साठ',
  'इकसठ',
  'बासठ',
  'तिरेसठ',
  'चौंसठ',
  'पैंसठ',
  'छियासठ',
  'सड़सठ',
  'अड़सठ',
  'उनहत्तर',
  'सत्तर',
  'इकहत्तर',
  'बहत्तर',
  'तिहत्तर',
  'चौहत्तर',
  'पचहत्तर',
  'छिहत्तर',
  'सतहत्तर',
  'अठहत्तर',
  'उन्यासी',
  'अस्सी',
  'इक्यासी',
  'बयासी',
  'तिरासी',
  'चौरासी',
  'पचासी',
  'छियासी',
  'सत्तासी',
  'अट्ठासी',
  'नवासी',
  'नब्बे',
  'इक्यानवे',
  'बानवे',
  'तिरानवे',
  'चौरानवे',
  'पचानवे',
  'छियानवे',
  'सत्तानवे',
  'अट्ठानवे',
  'निन्यानवे',
] as const

/** A whole number in Hindi words (0 → "शून्य"). */
export function hindiNumber(n: number): string {
  if (!Number.isSafeInteger(n) || n < 0) throw new RangeError(`not a whole number: ${n}`)
  if (n < 100) return UPTO_99[n]!
  const parts: string[] = []
  const take = (unit: number, word: string) => {
    const q = Math.floor(n / unit)
    if (q > 0) {
      parts.push(`${hindiNumber(q)} ${word}`)
      n -= q * unit
    }
  }
  take(10_000_000, 'करोड़')
  take(100_000, 'लाख')
  take(1_000, 'हज़ार')
  take(100, 'सौ')
  if (n > 0) parts.push(UPTO_99[n]!)
  return parts.join(' ')
}

/** Paise → "… रुपये … पैसे" (no paise part when it is zero). */
export function hindiRupees(paise: number): string {
  const r = Math.floor(paise / 100)
  const p = paise % 100
  const rupees = `${hindiNumber(r)} रुपये`
  return p ? `${rupees} ${hindiNumber(p)} पैसे` : rupees
}

const ONES = [
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
  'twelve',
  'thirteen',
  'fourteen',
  'fifteen',
  'sixteen',
  'seventeen',
  'eighteen',
  'nineteen',
] as const
const TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety']

/** A whole number in English words, Indian grouping (crore, lakh, thousand, hundred). */
export function englishNumber(n: number): string {
  if (!Number.isSafeInteger(n) || n < 0) throw new RangeError(`not a whole number: ${n}`)
  if (n < 20) return ONES[n]!
  if (n < 100) return TENS[Math.floor(n / 10)]! + (n % 10 ? `-${ONES[n % 10]}` : '')
  const parts: string[] = []
  const take = (unit: number, word: string) => {
    const q = Math.floor(n / unit)
    if (q > 0) {
      parts.push(`${englishNumber(q)} ${word}`)
      n -= q * unit
    }
  }
  take(10_000_000, 'crore')
  take(100_000, 'lakh')
  take(1_000, 'thousand')
  take(100, 'hundred')
  if (n > 0) parts.push(englishNumber(n))
  return parts.join(' ')
}

/** Paise → "… rupees … paise" (no paise part when it is zero). */
export function englishRupees(paise: number): string {
  const r = Math.floor(paise / 100)
  const p = paise % 100
  const rupees = `${englishNumber(r)} ${r === 1 ? 'rupee' : 'rupees'}`
  return p ? `${rupees} ${englishNumber(p)} paise` : rupees
}
